import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ReactNode } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { DashboardPage } from './pages/DashboardPage';
import { QrListPage } from './pages/QrListPage';
import { QrEditorPage } from './pages/QrEditorPage';
import { QrDetailPage } from './pages/QrDetailPage';
import { PageListPage } from './pages/PageListPage';
import { PageEditorPage } from './pages/PageEditorPage';
import { PagePreviewPage } from './pages/PagePreviewPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { PublicPage } from './pages/PublicPage';

const authed = (el: ReactNode) => (
  <ProtectedRoute>
    <Layout>{el}</Layout>
  </ProtectedRoute>
);

/**
 * `/` shows the public marketing landing for cold visitors and silently
 * forwards authenticated users to their dashboard so they don't get a
 * confusing "are you signed in?" page on every visit.
 */
function HomeRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/p/:slug" element={<PublicPage />} />

          <Route path="/dashboard" element={authed(<DashboardPage />)} />
          <Route path="/qrs" element={authed(<QrListPage />)} />
          <Route path="/qrs/new" element={authed(<QrEditorPage />)} />
          <Route path="/qrs/:qrId" element={authed(<QrDetailPage />)} />
          <Route path="/qrs/:qrId/edit" element={authed(<QrEditorPage />)} />
          <Route path="/pages" element={authed(<PageListPage />)} />
          <Route path="/pages/new" element={authed(<PageEditorPage />)} />
          <Route path="/pages/:pageId" element={authed(<PageEditorPage />)} />
          <Route
            path="/pages/:pageId/preview"
            element={
              <ProtectedRoute>
                <PagePreviewPage />
              </ProtectedRoute>
            }
          />
          <Route path="/analytics" element={authed(<AnalyticsPage />)} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
